import { createContext, useContext, type ReactNode } from 'react';

import { type AuthState, useAuthState } from './use-auth-state';

// useAuthState was being called independently in _layout.tsx,
// preview/index.tsx, and sign-up/index.tsx -- three separate
// onAuthStateChange subscriptions and three separately-initialized
// `session` useStates, each starting at `undefined` (loading) on
// mount. The root layout already gates all screen content on its own
// auth.status !== 'loading', but a child screen's own independent
// subscription hadn't necessarily caught up to the same status yet by
// the time it rendered -- SignUp's method-picker/age-gate branch could
// flash the wrong one for a frame. One subscription, provided once,
// fixes this at the root instead of patching each symptom.
type AuthContextValue = { state: AuthState; clearPasswordRecovery: () => void };

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { state, clearPasswordRecovery } = useAuthState();

  return <AuthContext.Provider value={{ state, clearPasswordRecovery }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx.state;
}

// Separate from useAuth() because only the password-recovery screen
// itself needs a way to leave that state (on save, or on cancel) --
// every other consumer only ever reads auth.status.
export function useClearPasswordRecovery(): () => void {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useClearPasswordRecovery must be used within an AuthProvider');
  return ctx.clearPasswordRecovery;
}
